import { MarkerClusterer } from '@googlemaps/markerclusterer'
import { Tween } from '@tweenjs/tween.js'
import { map as mapObject } from 'lodash-es'
import isEqual from 'lodash-es/isEqual'
import isFunction from 'lodash-es/isFunction'
import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import hash from 'stable-hash'

import { MapContext } from '@/feature/map/context/map'
import { useOptimizedLatLng } from '@/feature/map/hooks/useOptimizedLatLng'
import { addAnimation, Easing } from '@/utils/animation'

import styles from './MapMarker.module.css'

type eventType = {
  [K in keyof HTMLElementEventMap as `on${K}`]?: (
    this: HTMLElement,
    e: HTMLElementEventMap[K],
  ) => unknown
}

export type AssetMapMarkerProps = {
  children?: ReactNode
  position?: google.maps.marker.AdvancedMarkerElementOptions['position']
  markerOptions?: Omit<google.maps.marker.AdvancedMarkerElementOptions, 'position' | 'map'>
  className?: string
  shadowRootCss?: string
  zIndex?: number

  animate?: boolean

  clusterer?: boolean | MarkerClusterer

  onClick?: (e: google.maps.marker.AdvancedMarkerClickEvent) => void
  onDragEnd?: (e: google.maps.MapMouseEvent) => void
  onDragStart?: (e: google.maps.MapMouseEvent) => void
  onDrag?: (e: google.maps.MapMouseEvent) => void
} & Omit<eventType, 'onClick' | 'onDragEnd' | 'onDragStart' | 'onDrag'>

export const MapMarkerRender = (
  {
    children,
    markerOptions,
    position,
    className,
    shadowRootCss,
    zIndex,
    animate = true,
    clusterer: _clusterer = true,
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    ...props
  }: AssetMapMarkerProps,
  ref: ForwardedRef<google.maps.marker.AdvancedMarkerElement | undefined>,
) => {
  const map = useContext(MapContext)
  const [googleMarker, setGoogleMarker] = useState<google.maps.marker.AdvancedMarkerElement>()

  const optionsHash = hash(markerOptions)
  const positionHash = hash(position)

  const OptimizedLatLng = useOptimizedLatLng()
  const optimizedPosition = useMemo(() => new OptimizedLatLng(position ?? { lat: 0, lng: 0 }), [])

  const shadowStyle = useRef<HTMLStyleElement | null>(null)

  const isValidPosition = (
    pos: google.maps.marker.AdvancedMarkerElementOptions['position'],
  ): boolean => {
    if (pos == null) return false
    if (typeof pos === 'object' && 'lat' in pos && 'lng' in pos) {
      const lat =
        typeof pos.lat === 'function'
          ? (pos as google.maps.LatLng).lat()
          : (pos as google.maps.LatLngLiteral).lat
      const lng =
        typeof pos.lng === 'function'
          ? (pos as google.maps.LatLng).lng()
          : (pos as google.maps.LatLngLiteral).lng
      return isFinite(lat) && isFinite(lng)
    }
    return false
  }

  useEffect(() => {
    if (map?.map) {
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: isValidPosition(position) ? position : undefined,
        map: map.map,
        gmpClickable: !!onClick,
        gmpDraggable: !!(onDrag || onDragStart || onDragEnd),
        zIndex,
        ...markerOptions,
      })

      marker.classList.add(styles.noOutline)

      setGoogleMarker(marker)

      if (shadowRootCss) {
        marker.addEventListener('shadowRootOpened', () => {
          setTimeout(() => {
            const style = document.createElement('style')
            style.innerHTML = shadowRootCss
            marker.shadowRoot?.appendChild(style)
            shadowStyle.current = style
          }, 0)
        })
      }

      if (ref) {
        if (isFunction(ref)) {
          ref(marker)
        } else {
          ref.current = marker
        }
      }

      return () => {
        // Always detach on cleanup. Gating this on `marker.element?.isConnected` leaked markers
        // under React StrictMode (dev): the double-invoked effect runs this cleanup synchronously,
        // before Google Maps has asynchronously inserted the marker's element into the DOM, so the
        // element isn't connected yet, the removal was skipped, and an orphaned marker was left
        // overlapping the real one. Setting `map = null` is always safe.
        if (marker.map) marker.map = null
        setGoogleMarker(undefined)
        if (ref) {
          if (isFunction(ref)) {
            ref(undefined)
          } else {
            ref.current = undefined
          }
        }
      }
    }
  }, [map?.map, optionsHash])

  useEffect(() => {
    if (className) {
      googleMarker?.classList.add(...className.split(' ').filter((c) => c.trim().length > 0))

      return () => {
        googleMarker?.classList.remove(...className.split(' ').filter((c) => c.trim().length > 0))
      }
    }
  }, [className, googleMarker])

  useEffect(() => {
    if (shadowRootCss && googleMarker) {
      googleMarker.addEventListener('shadowRootOpened', () => {
        setTimeout(() => {
          const style = shadowStyle.current ?? document.createElement('style')
          style.innerHTML = shadowRootCss
          googleMarker.shadowRoot?.appendChild(style)
          shadowStyle.current = style
        }, 0)
      })
    }
  }, [shadowRootCss, googleMarker])

  useEffect(() => {
    if (googleMarker) {
      googleMarker.zIndex = zIndex
    }
  }, [zIndex, googleMarker])

  const animation = useRef<Tween<google.maps.LatLngLiteral> | null>(null)

  useEffect(() => {
    if (position && googleMarker && !isEqual(googleMarker.position, position)) {
      if (
        animate &&
        googleMarker.position &&
        googleMarker.map &&
        Object.values(
          googleMarker.element.getBoundingClientRect().toJSON() as Record<string, number>,
        ).reduce((acc, val) => acc + val, 0) > 0
      ) {
        // marker position should always be a LatLng object when reading but allows for LatLngLiteral when setting
        optimizedPosition.latitude = (googleMarker.position as google.maps.LatLngLiteral).lat
        optimizedPosition.longitude = (googleMarker.position as google.maps.LatLngLiteral).lng

        const oldAnimation = animation.current
        const newAnimation = new Tween(optimizedPosition.toJSON())
          .to(new window.google.maps.LatLng(position).toJSON())
          .onStart(() => {
            oldAnimation?.stop()
          })
          .easing(Easing.Quadratic.Out)
          .onUpdate((v) => {
            if (googleMarker) {
              optimizedPosition.latitude = v.lat
              optimizedPosition.longitude = v.lng
              googleMarker.position = optimizedPosition
            }
          })
          .onComplete(() => {
            animation.current = null
          })
          .start()

        animation.current = newAnimation
        addAnimation(newAnimation)
      } else {
        // This should never happen but just in case
        googleMarker.position = position
      }
    }
  }, [positionHash, googleMarker, animate])

  useEffect(() => {
    const marker = googleMarker
    if (marker) {
      const listeners = [
        onClick &&
          (marker.addEventListener('gmp-click', onClick),
          {
            remove: () => marker.removeEventListener('gmp-click', onClick),
          }),
        onDrag && marker.addListener('drag', onDrag),
        onDragStart && marker.addListener('dragstart', onDragStart),
        onDragEnd && marker.addListener('dragend', onDragEnd),
      ]

      marker.gmpClickable = !!onClick
      marker.gmpDraggable = !!(onDrag || onDragStart || onDragEnd)
      marker.draggable = !!(onDrag || onDragStart || onDragEnd)

      return () => {
        for (const listener of listeners) {
          listener?.remove()
        }
      }
    }
  }, [googleMarker, onClick, onDrag, onDragStart, onDragEnd])

  useEffect(() => {
    const marker = googleMarker
    if (marker) {
      const clearListeners = mapObject(props, (value, key) => {
        if (key.startsWith('on')) {
          const event = key.slice(2).toLowerCase()
          const handler = value as EventListenerOrEventListenerObject
          marker.addEventListener(event, handler)
          return () => marker.removeEventListener(event, handler)
        }
      })

      return () => {
        for (const clearListener of Object.values(clearListeners)) {
          clearListener?.()
        }
      }
    }
  }, [googleMarker, props])

  return isValidPosition(position) && googleMarker ? createPortal(children, googleMarker) : null
}

export const MapMarker = forwardRef(MapMarkerRender)
